import { EmptyStatesDoqContainer } from '@ardoq/graph';
import { DoqType, ResponsiveDoqWithSpeechBubble } from '@ardoq/doq';
import { FlexBox, Stack } from '@ardoq/layout';

import { Icon, IconName } from '@ardoq/icons';
import { colors } from '@ardoq/design-tokens';
import { connect } from '@ardoq/rxbeach';
import { map } from 'rxjs';
import { t, type I18nLocale, type WithI18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';

type NoReferencesDoqProps = {
  referenceType: string;
  i18nLocale: I18nLocale;
};

const NoReferencesDoqInner = ({
  referenceType,
  i18nLocale,
}: NoReferencesDoqProps) => (
  <EmptyStatesDoqContainer
    data-testid={`${DoqType.TREEMAP_ERROR}-emptystate__no-references`}
  >
    <ResponsiveDoqWithSpeechBubble
      doqType={DoqType.TREEMAP_ERROR}
      title={t(
        i18nLocale,
        'tabview:treemapView.emptyState.noReferences.title',
        'There is no data to show'
      )}
      message={t(
        i18nLocale,
        'tabview:treemapView.emptyState.noReferences.message',
        { referenceType },
        "Selected components don't have any {{referenceType}} references. Size components by a different attribute to show them in Treemap."
      )}
    />
  </EmptyStatesDoqContainer>
);

export const NoReferencesDoq = connect(
  NoReferencesDoqInner,
  i18nLocale$.pipe(map(i18nLocale => ({ i18nLocale })))
);

type NoSelectedUnusedFieldDoqProps = {
  fieldName: string;
  i18nLocale: I18nLocale;
};

const NoSelectedUnusedFieldDoqInner = ({
  fieldName,
  i18nLocale,
}: NoSelectedUnusedFieldDoqProps) => (
  <EmptyStatesDoqContainer
    data-testid={`${DoqType.TREEMAP_ERROR}-emptystate__no-selected-unused-field`}
  >
    <ResponsiveDoqWithSpeechBubble
      doqType={DoqType.TREEMAP_ERROR}
      title={t(
        i18nLocale,
        'tabview:treemapView.emptyState.noSelectedUnusedField.title',
        'There is no data to show'
      )}
      message={t(
        i18nLocale,
        'tabview:treemapView.emptyState.noSelectedUnusedField.message',
        { fieldName },
        'Selected components don’t use field {{fieldName}}. Size components by a different field to show them in Treemap.'
      )}
    />
  </EmptyStatesDoqContainer>
);

export const NoSelectedUnusedFieldDoq = connect(
  NoSelectedUnusedFieldDoqInner,
  i18nLocale$.pipe(map(i18nLocale => ({ i18nLocale })))
);

type SizeAttributeTextProps = WithI18nLocale;

const SizeAttributeText = ({ i18nLocale }: SizeAttributeTextProps) => (
  <>
    “
    <Icon
      iconName={IconName.FORMAT_LIST_BULLETED}
      style={{ verticalAlign: 'bottom', color: colors.grey400 }}
    />
    &nbsp;
    {t(
      i18nLocale,
      'tabview:treemapView.emptyState.sizeAttributeText',
      'Select size attribute'
    )}
    ”
  </>
);

type NoDescendantsDoqProps = WithI18nLocale;

const NoDescendantsDoqInner = ({ i18nLocale }: NoDescendantsDoqProps) => (
  <EmptyStatesDoqContainer
    data-testid={`${DoqType.TREEMAP_ERROR}-emptystate__no-descendents`}
  >
    <ResponsiveDoqWithSpeechBubble
      doqType={DoqType.TREEMAP_ERROR}
      title={t(
        i18nLocale,
        'tabview:treemapView.emptyState.noDescendants.title',
        'No descendants to show'
      )}
      message={
        <Stack gap="xlarge">
          <FlexBox gap="medium">
            {t(
              i18nLocale,
              'tabview:treemapView.emptyState.noDescendants.message1',
              'The selected components have no descendants.'
            )}
          </FlexBox>
          <div>
            {t(
              i18nLocale,
              'tabview:treemapView.emptyState.noDescendants.message2',
              'Select a component with at least one descendant in the navigator on the left or change the size attribute in view modifier'
            )}
            &nbsp;
            <SizeAttributeText i18nLocale={i18nLocale} />
          </div>
        </Stack>
      }
    />
  </EmptyStatesDoqContainer>
);

export const NoDescendantsDoq = connect(
  NoDescendantsDoqInner,
  i18nLocale$.pipe(map(i18nLocale => ({ i18nLocale })))
);

type NoPositiveValuesDoqProps = {
  activeFieldLabel?: string;
  i18nLocale: I18nLocale;
};

const NoPositiveValuesDoqInner = ({
  activeFieldLabel,
  i18nLocale,
}: NoPositiveValuesDoqProps) => (
  <EmptyStatesDoqContainer
    data-testid={`${DoqType.TREEMAP_ERROR}-emptystate__no-positive-values`}
  >
    <ResponsiveDoqWithSpeechBubble
      doqType={DoqType.TREEMAP_ERROR}
      title={t(
        i18nLocale,
        'tabview:treemapView.emptyState.noPositiveValues.title',
        'No visible value to show'
      )}
      message={
        <Stack gap="xlarge">
          <FlexBox gap="medium">
            {activeFieldLabel
              ? t(
                  i18nLocale,
                  'tabview:treemapView.emptyState.noPositiveValues.message1WithField',
                  { activeFieldLabel },
                  'The selected components have numbers with a negative value in the field - {{activeFieldLabel}}'
                )
              : t(
                  i18nLocale,
                  'tabview:treemapView.emptyState.noPositiveValues.message1',
                  'The selected components have numbers with a negative value in the field'
                )}
          </FlexBox>
          <div>
            {t(
              i18nLocale,
              'tabview:treemapView.emptyState.noPositiveValues.message2',
              'To use this Treemap, select a component with a field number greater than 0 or change the size attribute in view modifier'
            )}
            &nbsp;
            <SizeAttributeText i18nLocale={i18nLocale} />
          </div>
        </Stack>
      }
    />
  </EmptyStatesDoqContainer>
);

export const NoPositiveValuesDoq = connect(
  NoPositiveValuesDoqInner,
  i18nLocale$.pipe(map(i18nLocale => ({ i18nLocale })))
);
